import { useEffect, useCallback, useRef } from 'react';
import type { Strategy, StrategyFamily } from '../types/Strategy';
import type { StrategyNode } from '../types/BaseNode';
import { ApiService } from '../services/ApiService';
import { serializeNode } from '../utils/serializeNode';

import type { WorkspaceState, StrategyResult } from './useWorkspaceState';
import { toast } from 'sonner';

import { useStrategyCrud } from './useStrategyCrud';
import { useQuantArcJobs } from './useQuantArcJobs';
import { useMeepMeepJobs } from './useMeepMeepJobs';
import { normalizeNodeMetadata } from '../utils/metadataUtils';

interface UseStrategyOperationsParams {
    strategies: Strategy[];
    setStrategies: React.Dispatch<React.SetStateAction<Strategy[]>>;
    setActiveStrategyId: React.Dispatch<React.SetStateAction<string | 'all' | null>>;
    setActiveTab: React.Dispatch<React.SetStateAction<'build' | 'results' | 'monitor' | 'alerts' | 'guide'>>;
    setWorkspaceState: (patch: Partial<WorkspaceState>) => void;
    setStrategyResult: (id: string, result: Partial<StrategyResult>) => void;
    removeStrategyResult: (id: string) => void;
    globalStart: string | null;
    globalEnd: string | null;
    strategyFamily: StrategyFamily;
    initialState?: Partial<{ strategies: Strategy[] }>;
}

export function useStrategyOperations({
    strategies,
    setStrategies,
    setActiveStrategyId,
    setActiveTab,
    setWorkspaceState,
    setStrategyResult,
    removeStrategyResult,
    globalStart,
    globalEnd,
    strategyFamily,
    initialState,
}: UseStrategyOperationsParams) {
    const startStrategyLoading = useCallback((strategyId: string, mode: 'evaluate' | 'quantarc' | 'meepmeep') => {
        setStrategyResult(strategyId, {
            result: null,
            error: null,
            nature: undefined,
            display_type: undefined,
            categorical_labels: undefined,
            backendStats: null,
            chartData: null,
            chartSeriesLabels: null,
            nodeMetadata: undefined,
            parityReport: null,
            isLoading: true,
            loadingMode: mode,
            quantArcJobId: null,
            quantArcJobStatus: null,
            quantArcJobMessage: null,
            meepMeepJobId: null,
            meepMeepJobStatus: null,
            meepMeepJobMessage: null,
            meepMeepJobStage: null,
            meepMeepCurrentDayCount: null,
            meepMeepTotalDayCount: null,
            meepMeepProgressPct: null,
        });
    }, [setStrategyResult]);

    const stopStrategyLoading = useCallback((strategyId: string, error?: string | null) => {
        setStrategyResult(strategyId, {
            isLoading: false,
            loadingMode: null,
            ...(error !== undefined ? { error } : {}),
        });
    }, [setStrategyResult]);

    const crud = useStrategyCrud({
        strategies,
        setStrategies,
        setActiveStrategyId,
        setActiveTab,
        removeStrategyResult,
        initialState,
        getCurrentStrategyFamily: () => strategyFamily,
    });

    const quantArc = useQuantArcJobs({
        strategies,
        setWorkspaceState,
        setStrategyResult,
        globalStart,
        globalEnd,
        startStrategyLoading,
        stopStrategyLoading,
    });

    const meepMeep = useMeepMeepJobs({
        strategies,
        setWorkspaceState,
        setStrategyResult,
    });

    // ── Evaluate Routine (Orchestrator Level) ────────────────────────────────
    const evaluateStrategy = useCallback(async (strategy: Strategy, nodeOverride?: StrategyNode | null) => {
        setWorkspaceState({ errorNode: null });

        try {
            const nodeToEvaluate = nodeOverride ?? strategy.nodes[0];
            if (!nodeToEvaluate) {
                stopStrategyLoading(strategy.id);
                return;
            }

            if (nodeToEvaluate.type === 'MeepMeep') {
                startStrategyLoading(strategy.id, 'meepmeep');
                const serialized = serializeNode(nodeToEvaluate, globalStart, globalEnd);
                const evalContext: Record<string, any> = { start_date: globalStart, end_date: globalEnd };
                const json = await ApiService.startMeepMeepJob(
                    serialized,
                    evalContext as { start_date: string | null; end_date: string | null },
                );

                if (json.success === false || !json.job_id) {
                    setStrategyResult(strategy.id, {
                        result: null,
                        error: json.error?.error ?? 'MeepMeep evaluation failed to start.',
                        nature: 'meepmeep_result',
                        backendStats: null,
                        chartData: null,
                        chartSeriesLabels: null,
                        isLoading: false,
                        loadingMode: null,
                        meepMeepJobId: null,
                        meepMeepJobStatus: 'failed',
                        meepMeepJobMessage: json.message ?? null,
                        meepMeepJobStage: null,
                        meepMeepCurrentDayCount: null,
                        meepMeepTotalDayCount: null,
                        meepMeepProgressPct: null,
                    });
                    setWorkspaceState({ activeTab: 'results' });
                    return;
                }

                setStrategyResult(strategy.id, {
                    result: null,
                    error: null,
                    nature: 'meepmeep_result',
                    backendStats: null,
                    chartData: null,
                    chartSeriesLabels: null,
                    meepMeepRequestTree: serialized,
                    isLoading: true,
                    loadingMode: 'meepmeep',
                    meepMeepJobId: json.job_id,
                    meepMeepJobStatus: json.status,
                    meepMeepJobMessage: json.message ?? null,
                    meepMeepJobStage: json.current_stage ?? null,
                    meepMeepCurrentDayCount: json.current_day_count ?? 0,
                    meepMeepTotalDayCount: json.total_day_count ?? null,
                    meepMeepProgressPct: json.progress_pct ?? 0,
                });
                setWorkspaceState({ activeTab: 'results' });
                return;
            }

            startStrategyLoading(strategy.id, 'evaluate');
            const serialized = serializeNode(nodeToEvaluate, globalStart, globalEnd);
            const evalContext: Record<string, any> = { start_date: globalStart, end_date: globalEnd };

            const json = await ApiService.evaluateStrategy(
                serialized,
                evalContext as { start_date: string | null; end_date: string | null },
                nodeToEvaluate
            );

            if (json.node_id && json.message) {
                setWorkspaceState({
                    errorNode: {
                        nodeId: json.node_id,
                        message: json.message,
                        hint: json.hint ?? '',
                    },
                    activeTab: 'build',
                });
                stopStrategyLoading(strategy.id);
                return;
            }

            if (json.error) {
                setStrategyResult(strategy.id, {
                    result: [],
                    error: json.error,
                    isLoading: false,
                    loadingMode: null,
                });
                setWorkspaceState({ activeTab: 'results' });
                return;
            }

            const resultData = (json.nature === 'insight' || json.nature === 'meepmeep_result') ? json : (json.data ?? null);

            setStrategyResult(strategy.id, {
                result: resultData,
                error: null,
                nature: json.nature ?? 'generic',
                display_type: json.display_type ?? undefined,
                categorical_labels: json.categorical_labels ?? undefined,
                backendStats: json.stats ?? null,
                chartData: json.chart_data ?? null,
                chartSeriesLabels: json.chart_series_labels ?? null,
                nodeMetadata: normalizeNodeMetadata(json.node_metadata, json, nodeToEvaluate.id),
                parityReport: json.parity_report ?? null,
                isLoading: false,
                loadingMode: null,
            });

            setWorkspaceState({ activeTab: 'results' });

        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Network error';
            setStrategyResult(strategy.id, {
                result: null,
                error: errorMessage,
                isLoading: false,
                loadingMode: null,
            });
            setWorkspaceState({ activeTab: 'results' });
        }
    }, [globalStart, globalEnd, strategyFamily, setWorkspaceState, startStrategyLoading, stopStrategyLoading, setStrategyResult]);


    // ── Global Polling Effect ───────────────────────────────────────────────────
    useEffect(() => {
        const handle = window.setInterval(() => {
            void quantArc.pollQuantArcJobs();
        }, 2_000);
        return () => window.clearInterval(handle);
    }, [quantArc.pollQuantArcJobs]);

    useEffect(() => {
        const handle = window.setInterval(() => {
            void meepMeep.pollMeepMeepJobs();
        }, 5_000);
        return () => window.clearInterval(handle);
    }, [meepMeep.pollMeepMeepJobs]);

    const activeQuantArcJobsRef = useRef<string[]>([]);
    useEffect(() => {
        activeQuantArcJobsRef.current = strategies
            .filter((strategy) => strategy.loadingMode === 'quantarc' && strategy.quantArcJobId)
            .map((strategy) => strategy.quantArcJobId)
            .filter((jobId): jobId is string => Boolean(jobId));
    }, [strategies]);

    const activeMeepMeepJobsRef = useRef<string[]>([]);
    useEffect(() => {
        activeMeepMeepJobsRef.current = strategies
            .filter((strategy) => strategy.loadingMode === 'meepmeep' && strategy.meepMeepJobId)
            .map((strategy) => strategy.meepMeepJobId)
            .filter((jobId): jobId is string => Boolean(jobId));
    }, [strategies]);

    useEffect(() => {
        const activeJobsKey = activeQuantArcJobsRef.current.slice().sort().join(',');
        if (activeJobsKey && activeJobsKey !== quantArc.activeQuantArcJobsKeyRef.current) {
            void quantArc.pollQuantArcJobs();
        }
        quantArc.activeQuantArcJobsKeyRef.current = activeJobsKey;
    }, [strategies, quantArc]);

    useEffect(() => {
        const activeJobsKey = activeMeepMeepJobsRef.current.slice().sort().join(',');
        if (activeJobsKey && activeJobsKey !== meepMeep.activeMeepMeepJobsKeyRef.current) {
            void meepMeep.pollMeepMeepJobs();
        }
        meepMeep.activeMeepMeepJobsKeyRef.current = activeJobsKey;
    }, [strategies, meepMeep]);


    // ── Extras & Tooling ────────────────────────────────────────────────────────
    const refreshOverlays = useCallback(async (strategy: Strategy) => {
        if (!strategy.overlays || strategy.overlays.length === 0) return;

        const resultsPatch: Record<string, any> = {};
        let hasNewResults = false;

        await Promise.all(strategy.overlays.map(async (o) => {
            try {
                if (strategy.result && !o.result) {
                    const res = await ApiService.calculateOverlay({
                        type_id: o.id,
                        params: o.params,
                        data: strategy.result
                    });
                    resultsPatch[o.id] = res;
                    hasNewResults = true;
                }
            } catch (err) {
                console.error(`Failed to refresh overlay ${o.name}:`, err);
            }
        }));

        if (hasNewResults) {
            setStrategyResult(strategy.id, {
                overlayResults: {
                    ...(strategy.overlayResults || {}),
                    ...resultsPatch
                }
            } as any);
        }
    }, [setStrategyResult]);

    const updateMonitorLogic = useCallback(async (strategy: Strategy) => {
        if (!strategy.monitorId || !strategy.nodes.length) return;

        setWorkspaceState({ isEvaluating: true });
        try {
            const rootNode = strategy.nodes[0];
            const strategyJson = serializeNode(rootNode, globalStart, globalEnd);

            await ApiService.updateAlertLogic(String(strategy.monitorId), {
                strategy_json: strategyJson,
                condition_json: strategyJson
            });
            toast.success('Monitor updated successfully!');
        } catch (err: unknown) {
            console.error(err);
            toast.error('Failed to update monitor');
        } finally {
            setWorkspaceState({ isEvaluating: false });
        }
    }, [globalStart, globalEnd, setWorkspaceState]);

    return {
        ...crud,
        evaluateStrategy,
        runQuantArcBacktest: quantArc.runQuantArcBacktest,
        cancelQuantArcBacktest: quantArc.cancelQuantArcBacktest,
        cancelMeepMeepJob: meepMeep.cancelMeepMeepJob,
        refreshOverlays,
        updateMonitorLogic,
    };
}
